import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Compass, ChevronDown, ChevronRight, X, Moon, Sun, LogOut, User, 
  Activity, Brain, Shield, BookOpen, ClipboardList,
  Timer, Edit2, Check, Lightbulb,
  HeartHandshake, Map, Waves, Palette, FolderOpen, BarChart3
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePremiumStatus } from '@/hooks/usePremiumStatus';
import { toast } from 'sonner';
import SupportResourcesModal from '@/components/SupportResourcesModal';
import GuidedModeInfoModal from '@/components/GuidedModeInfoModal';
import PaywallModal from '@/components/PaywallModal';
import LockedOverlay from '@/components/LockedOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Accordion data structure
const ACCORDION_SECTIONS = [
  {
    id: 'calm',
    title: 'I need to calm down',
    subtitle: 'Quick tools to help your body settle',
    icon: Waves,
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    bgColorDark: 'rgba(16, 185, 129, 0.15)',
    primaryTools: [
      { name: 'Grounding Toolkit', description: 'Tools to stay present and regulated', path: '/grounding-toolkit', icon: Timer },
      { name: 'Body Map Tool', description: 'Identify and track physical sensations in your body', path: '/body-map', icon: Map, isPremium: true },
      { name: 'SUDS Chart Builder', description: 'Measure your anxiety levels using the SUDS scale', path: '/suds-chart-builder', icon: Activity }
    ],
    secondaryLabel: 'Explore additional practical resources',
    secondaryTools: [
      { name: 'Window of Tolerance', description: 'Learn about your Window of Tolerance', path: '/window-of-tolerance' },
      { name: 'Anxiety & Body SUDS', description: 'Learn how to measure Anxiety levels with SUDS', path: '/anxiety-body-suds' },
      { name: 'Healthy Relationships', description: 'Learn what Healthy Relationships look like', path: '/healthy-relationships' },
      { name: 'CalmGuard Tool', description: 'Create a CalmGuard plan for in-the-moment support', path: '/setup' }
    ]
  },
  {
    id: 'understand',
    title: 'I need to understand',
    subtitle: 'Learn what\'s happening and why',
    icon: Lightbulb,
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.08)',
    bgColorDark: 'rgba(59, 130, 246, 0.15)',
    primaryTools: [
      { name: 'Learning Modules', description: 'Explore educational content on narcissism and recovery', path: '/insights', icon: BookOpen },
      { name: 'Quizzes', description: 'Self-reflection quizzes to understand your patterns', path: '/quizzes', icon: ClipboardList }
    ],
    secondaryLabel: null,
    secondaryTools: []
  },
  {
    id: 'support',
    title: 'I need support',
    subtitle: 'Get help and guidance',
    icon: HeartHandshake,
    color: '#14b8a6',
    bgColor: 'rgba(20, 184, 166, 0.08)',
    bgColorDark: 'rgba(20, 184, 166, 0.15)',
    primaryTools: [
      { name: 'I Need Help Right Now', description: 'Access crisis support resources', path: null, action: 'supportModal', icon: Shield },
      { name: 'Guided Mode', description: 'Follow a structured, step-by-step path through the content', path: '/guided-mode', icon: Compass }
    ],
    secondaryLabel: 'Explore additional practical resources',
    secondaryTools: [
      { name: 'Finding a trauma-trained therapist', path: '/module/finding-a-trauma-trained-therapist' },
      { name: 'Work feels hostile or emotionally draining', path: '/insights', scrollToSection: 'workplace-dynamics' },
      { name: 'Communicating boundaries', path: '/communication-boundaries' },
      { name: 'Faith played a role in my hurt', path: '/insights', scrollToSection: 'what-god-says-about-abuse' }
    ]
  },
  {
    id: 'process',
    title: 'I need to process',
    subtitle: 'Reflect and make sense of things',
    icon: Brain,
    color: '#8b5cf6',
    bgColor: 'rgba(139, 92, 246, 0.08)',
    bgColorDark: 'rgba(139, 92, 246, 0.15)',
    primaryTools: [
      { name: 'Thought Fighting Tool', description: 'Challenge and reframe unhelpful thoughts', path: '/thought-fighting', icon: Brain },
      { name: 'CalmGuard Tool', description: 'Create a CalmGuard plan for in-the-moment support', path: '/setup', icon: Shield }
    ],
    secondaryLabel: 'Explore additional practical resources',
    secondaryTools: [
      { name: 'Personal Anchors', description: 'Select your personal coping anchors', path: '/values' },
      { name: 'Visual Anchor Creator', description: 'Create visual reminders for stability', path: '/wallpaper-generator' },
      { name: "The Narcissist's Playbook", description: 'Catalog and understand your triggers', path: '/trigger-library' },
      { name: 'Learning Modules', description: 'Active journaling prompts at the end of each module', path: '/insights' }
    ]
  }
];

// Rotating affirmations (same as free play mode)
const AFFIRMATIONS = [
  "Your healing journey is unique and valid.",
  "You deserve peace and safety.",
  "Trust your instincts — they're guiding you.",
  "Small steps forward are still progress.",
  "You are worthy of healthy relationships.",
  "It's okay to set boundaries.",
  "Your feelings matter.",
  "Healing isn't linear, and that's okay.",
  "You have survived 100% of your hardest days.",
  "Be gentle with yourself today."
];

// Resources Accordion Card Component - extracted for cleaner rendering
const ResourcesAccordionCard = ({ hubCardsSettings, cardOverlayUrls, isDarkMode, resourcesAccordionOpen, setResourcesAccordionOpen, navigate }) => {
  const resourcesCardSettings = hubCardsSettings?.resources;
  const hasCustomResourcesSettings = resourcesCardSettings?.background_color || resourcesCardSettings?.overlay_image_id;
  const resourcesOverlayUrl = cardOverlayUrls['resources'];
  
  let resourcesCardBg;
  if (resourcesCardSettings?.background_color) {
    const opacity = resourcesCardSettings.background_opacity ?? 0.8;
    const hex = resourcesCardSettings.background_color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    resourcesCardBg = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else if (!hasCustomResourcesSettings) {
    resourcesCardBg = isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'var(--bg-card)';
  } else {
    resourcesCardBg = 'transparent';
  }
  
  return (
    <Card 
      className="overflow-hidden transition-all duration-300"
      style={{ 
        background: resourcesCardBg,
        backdropFilter: 'blur(10px)',
        border: `2px solid ${isDarkMode ? '#6366f1' : 'rgba(99, 102, 241, 0.5)'}`,
        position: 'relative',
        boxShadow: isDarkMode 
          ? '0 4px 14px -3px rgba(0, 0, 0, 0.4), 0 2px 6px -2px rgba(0, 0, 0, 0.3)'
          : '0 4px 14px -3px rgba(0, 0, 0, 0.1), 0 2px 6px -2px rgba(0, 0, 0, 0.06)'
      }}
      data-testid="resources-accordion-card"
      data-custom-card={hasCustomResourcesSettings ? "true" : undefined}
    >
      {/* Overlay Image for resources card */}
      {resourcesOverlayUrl && (
        <div 
          className="absolute inset-0 bg-cover bg-center pointer-events-none"
          style={{ 
            backgroundImage: `url(${resourcesOverlayUrl})`,
            opacity: resourcesCardSettings?.overlay_opacity ?? 0.3,
            zIndex: 0
          }}
        />
      )}
      
      <button
        onClick={() => setResourcesAccordionOpen(!resourcesAccordionOpen)}
        className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-opacity-80 transition-colors relative"
        aria-expanded={resourcesAccordionOpen}
        aria-controls="resources-content"
        style={{ zIndex: 1 }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
            style={{ background: isDarkMode ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.15)' }}
          >
            <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#6366f1' }} />
          </div>
          <div className="text-left">
            <span className="font-semibold text-lg sm:text-xl block" style={{ color: 'var(--text-primary)' }}>
              Let's look at my resources
            </span>
            <span className="text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              Past plans and personalized tools
            </span>
          </div>
        </div>
        <ChevronDown 
          className={`w-5 h-5 transition-transform duration-300 ${resourcesAccordionOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
        />
      </button>
      
      <div 
        id="resources-content"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${resourcesAccordionOpen ? 'max-h-[60rem] sm:max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <CardContent className="pt-0 pb-6 sm:pb-5 px-4 sm:px-5">
          <div className="flex flex-col gap-3">
            {/* Past CalmGuard Plans */}
            <button
              onClick={() => navigate('/saved-plans')}
              className="p-4 rounded-lg border text-left hover:shadow-md transition-all flex items-center gap-3"
              style={{ 
                background: isDarkMode ? '#1a3330' : 'var(--bg-card)',
                borderColor: isDarkMode ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 0.3)'
              }}
              data-testid="past-calmguard-plans-btn"
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)' }}
              >
                <ClipboardList className="w-5 h-5" style={{ color: '#3b82f6' }} />
              </div>
              <div>
                <span className="font-medium block" style={{ color: 'var(--text-primary)' }}>
                  Past CalmGuard Plans
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Revisit steps that helped you feel steady and grounded
                </span>
              </div>
            </button>

            {/* Past Thought Fighting Plans */}
            <button
              onClick={() => navigate('/saved-thought-fighting')}
              className="p-4 rounded-lg border text-left hover:shadow-md transition-all flex items-center gap-3"
              style={{ 
                background: isDarkMode ? '#1a3330' : 'var(--bg-card)',
                borderColor: isDarkMode ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 0.3)'
              }}
              data-testid="past-thought-fighting-plans-btn"
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.15)' }}
              >
                <Brain className="w-5 h-5" style={{ color: '#a855f7' }} />
              </div>
              <div>
                <span className="font-medium block" style={{ color: 'var(--text-primary)' }}>
                  Past Thought Fighting Plans
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Check the strategies you've used to challenge unhelpful thoughts
                </span>
              </div>
            </button>

            {/* My Customized SUDS Chart */}
            <button
              onClick={() => navigate('/suds-summary')}
              className="p-4 rounded-lg border text-left hover:shadow-md transition-all flex items-center gap-3"
              style={{ 
                background: isDarkMode ? '#1a3330' : 'var(--bg-card)',
                borderColor: isDarkMode ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 0.3)'
              }}
              data-testid="my-suds-chart-btn"
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.15)' }}
              >
                <BarChart3 className="w-5 h-5" style={{ color: '#10b981' }} />
              </div>
              <div>
                <span className="font-medium block" style={{ color: 'var(--text-primary)' }}>
                  My Customized SUDS Chart
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Your personal chart for noticing, tracking, and coping with intensity
                </span>
              </div>
            </button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default function SimplifiedDashboard() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode, colorTheme, getCurrentCustomization } = useTheme();
  const { currentUser: user, logout } = useAuth();
  const { is_premium } = usePremiumStatus();
  
  // Admin email check
  const ADMIN_EMAIL = 'fox.rachel.christina@gmail.com';
  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  
  // State
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [openAccordion, setOpenAccordion] = useState(null);
  const [openSecondary, setOpenSecondary] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showGuidedModeInfo, setShowGuidedModeInfo] = useState(false);
  const [resourcesAccordionOpen, setResourcesAccordionOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Per-card overlay images (keyed by card id)
  const [cardOverlayUrls, setCardOverlayUrls] = useState({});
  
  // Get custom settings (includes preview mode support)
  const customization = getCurrentCustomization();
  const hubCardsSettings = customization?.hub_cards || null;
  
  // Load card overlay images for all cards (fetch by ID) - in parallel
  useEffect(() => {
    const loadCardOverlays = async () => {
      const cardIds = ['calm', 'understand', 'support', 'process', 'resources'];
      
      // Load all images in parallel
      const loadPromises = cardIds.map(async (cardId) => {
        const cardSettings = hubCardsSettings?.[cardId];
        
        if (cardSettings?.overlay_image_id) {
          try {
            const response = await fetch(`${BACKEND_URL}/api/theme-customizations/image/${cardSettings.overlay_image_id}`);
            if (response.ok) {
              const data = await response.json();
              console.log(`🎨 Loaded card ${cardId} overlay:`, cardSettings.overlay_image_id);
              return { cardId, url: data.data_url };
            }
          } catch (error) {
            console.error(`Error loading card ${cardId} overlay:`, error);
          }
        }
        return null;
      });
      
      const results = await Promise.all(loadPromises);
      const newOverlayUrls = {};
      results.forEach(result => {
        if (result) {
          newOverlayUrls[result.cardId] = result.url;
        }
      });
      
      setCardOverlayUrls(newOverlayUrls);
    };
    
    loadCardOverlays();
  }, [hubCardsSettings]);
  
  // Generate custom card style for a specific card
  const getCustomCardStyle = (section, isOpen) => {
    const cardSettings = hubCardsSettings?.[section.id];
    
    // Check if there are custom settings for this card
    const hasCustomSettings = cardSettings?.background_color || cardSettings?.overlay_image_id;
    
    const baseStyle = {
      transition: 'all 0.2s ease',
      boxShadow: isDarkMode 
        ? '0 4px 14px -3px rgba(0, 0, 0, 0.4), 0 2px 6px -2px rgba(0, 0, 0, 0.3)'
        : '0 4px 14px -3px rgba(0, 0, 0, 0.1), 0 2px 6px -2px rgba(0, 0, 0, 0.06)',
      border: isDarkMode 
        ? `2px solid ${section.color}` 
        : isOpen 
          ? `2px solid ${section.color}` 
          : `2px solid ${section.color}90`,
      position: 'relative',
      overflow: 'hidden'
    };
    
    // Apply custom background color if set for this specific card
    if (cardSettings?.background_color) {
      const opacity = cardSettings.background_opacity ?? 0.8;
      const hex = cardSettings.background_color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      baseStyle.background = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } else if (!hasCustomSettings) {
      // Only use default dark mode colors if no custom settings
      baseStyle.background = isDarkMode ? section.bgColorDark : section.bgColor;
    } else {
      // Has overlay but no background color - use transparent to show overlay
      baseStyle.background = 'transparent';
    }
    
    return baseStyle;
  };
  
  // Get card overlay URL for a specific card
  const getCardOverlayUrl = (cardId) => {
    return cardOverlayUrls[cardId] || null;
  };
  
  // Get card overlay opacity for a specific card
  const getCardOverlayOpacity = (cardId) => {
    const cardSettings = hubCardsSettings?.[cardId];
    return cardSettings?.overlay_opacity ?? 0.3;
  };
  
  // Button text color for dark mode
  const buttonTextColor = isDarkMode ? '#1a365d' : '#ffffff';
  
  // Rotating affirmation
  const [affirmation] = useState(() => {
    return AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];
  });
  
  // Initialize from localStorage
  const [userName, setUserName] = useState(() => {
    return localStorage.getItem('calmguard_user_name') || '';
  });
  const [quizResults, setQuizResults] = useState(() => {
    const savedResults = localStorage.getItem('calmguard_quiz_results');
    const dismissed = localStorage.getItem('calmguard_quiz_dismissed');
    if (savedResults && !dismissed) {
      try {
        return JSON.parse(savedResults);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [showRecommendations, setShowRecommendations] = useState(() => {
    const savedResults = localStorage.getItem('calmguard_quiz_results');
    const dismissed = localStorage.getItem('calmguard_quiz_dismissed');
    return !!(savedResults && !dismissed);
  });
  
  const isGuest = user?.isAnonymous || localStorage.getItem('isGuestUser') === 'true';

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleSaveName = () => {
    const trimmedName = tempName.trim();
    if (trimmedName) {
      setUserName(trimmedName);
      localStorage.setItem('calmguard_user_name', trimmedName);
      toast.success('Name saved!');
    }
    setIsEditingName(false);
  };

  const handleStartEditName = () => {
    setTempName(userName);
    setIsEditingName(true);
  };

  const handleAccordionClick = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
    setOpenSecondary(null); // Close secondary when switching
  };

  const handleSecondaryClick = (id) => {
    setOpenSecondary(openSecondary === id ? null : id);
  };

  const handleToolClick = (tool) => {
    // SINGLE SOURCE OF TRUTH: Check if tool is locked
    // isPremium = true AND userTier = free → locked, show paywall
    // isPremium = true AND userTier = paid → unlocked, proceed
    // isPremium = false → unlocked, proceed
    const isLocked = tool.isPremium && !is_premium;
    
    if (isLocked) {
      // Show paywall modal, do NOT navigate
      setShowPaywall(true);
      return;
    }
    
    if (tool.action === 'supportModal') {
      setShowSupportModal(true);
    } else if (tool.path) {
      if (tool.path === '/guided-mode' && isGuest) {
        setShowGuidedModeInfo(true);
      } else if (tool.scrollToSection) {
        // Navigate to path with section query param for auto-scrolling
        navigate(`${tool.path}?section=${tool.scrollToSection}`);
      } else {
        navigate(tool.path);
      }
    }
  };

  const handleDismissRecommendations = () => {
    setShowRecommendations(false);
    localStorage.setItem('calmguard_quiz_dismissed', 'true');
  };

  const handleTakeQuiz = () => {
    // Clear dismissed state when taking quiz again
    localStorage.removeItem('calmguard_quiz_dismissed');
    navigate('/where-to-start');
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="page-container">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        {/* Header - Mobile: buttons on top, title centered below. Desktop: side by side */}
        <div className="mb-2">
          {/* Buttons row - right aligned on mobile, part of flex on desktop */}
          <div className="flex justify-end gap-2 mb-3 sm:hidden">
            <Button
              onClick={() => navigate('/profile')}
              variant="outline"
              size="icon"
              title="My Profile"
              className="shrink-0"
            >
              <User className="w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/profile', { state: { scrollTo: 'appearance' } })}
              variant="outline"
              size="icon"
              title="Appearance Settings"
              className="shrink-0"
            >
              <Palette className="w-5 h-5" style={{ color: isDarkMode ? 'var(--accent-teal)' : '#1f2937' }} />
            </Button>
            <Button
              onClick={toggleDarkMode}
              variant="outline"
              size="icon"
              data-testid="dark-mode-toggle"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              className="shrink-0"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              title="Logout"
              className="shrink-0"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Desktop: title and buttons side by side */}
          <div className="hidden sm:flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Work Sans', color: 'var(--text-primary)' }}>
                Your CalmGuard Hub
              </h1>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                size="icon"
                title="My Profile"
                className="shrink-0"
              >
                <User className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => navigate('/profile', { state: { scrollTo: 'appearance' } })}
                variant="outline"
                size="icon"
                title="Appearance Settings"
                className="shrink-0"
              >
                <Palette className="w-5 h-5" style={{ color: isDarkMode ? 'var(--accent-teal)' : '#1f2937' }} />
              </Button>
              <Button
                onClick={toggleDarkMode}
                variant="outline"
                size="icon"
                data-testid="dark-mode-toggle"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                className="shrink-0"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="icon"
                title="Logout"
                className="shrink-0"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Mobile: title centered below buttons */}
          <div className="sm:hidden text-center">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans', color: 'var(--text-primary)' }}>
              Your CalmGuard Hub
            </h1>
          </div>
        </div>

        {/* Rotating Affirmation - centered on mobile */}
        <p className="text-base sm:text-lg mb-6 text-center sm:text-left" style={{ color: 'var(--text-secondary)' }}>
          {affirmation}
        </p>

        {/* Personalized Greeting */}
        <Card className="mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              {!isEditingName ? (
                <>
                  <div className="flex-1">
                    <p className="text-lg sm:text-xl" style={{ color: 'var(--text-primary)' }}>
                      {getGreeting()}{userName ? `, ${userName}` : ''}.
                    </p>
                    <p className="text-base sm:text-lg mt-1" style={{ color: 'var(--text-secondary)' }}>
                      What do you need right now?
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleStartEditName}
                    className="shrink-0"
                    title={userName ? 'Edit name' : 'Add your name'}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    placeholder="Enter your name"
                    className="flex-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                  />
                  <Button size="sm" onClick={handleSaveName}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* I Don't Know Where to Start Quiz Button */}
        {isDarkMode ? (
          <div
            onClick={handleTakeQuiz}
            role="button"
            tabIndex={0}
            className="w-full mb-6 py-6 text-base sm:text-lg rounded-md font-medium flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
              boxShadow: '0 4px 14px -3px rgba(0, 0, 0, 0.4), 0 2px 6px -2px rgba(0, 0, 0, 0.3)'
            }}
            data-testid="where-to-start-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a365d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
            </svg>
            <span className="quiz-btn-dark-text">I Don't Know Where to Begin</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a365d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        ) : (
          <button
            onClick={handleTakeQuiz}
            className="w-full mb-6 py-6 text-base sm:text-lg rounded-md font-medium flex items-center justify-center text-white"
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
              boxShadow: '0 4px 14px -3px rgba(0, 0, 0, 0.15), 0 2px 6px -2px rgba(0, 0, 0, 0.1)'
            }}
            data-testid="where-to-start-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
            </svg>
            I Don't Know Where to Begin
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        )}

        {/* Quiz Results Recommendations Card */}
        {quizResults && showRecommendations && (
          <Card 
            className="mb-6 shadow-md" 
            style={{ 
              background: 'var(--bg-card)', 
              border: `2px solid ${quizResults.color || 'var(--primary-blue)'}`,
              boxShadow: isDarkMode 
                ? '0 4px 14px -3px rgba(0, 0, 0, 0.4), 0 2px 6px -2px rgba(0, 0, 0, 0.3)'
                : '0 4px 14px -3px rgba(0, 0, 0, 0.1), 0 2px 6px -2px rgba(0, 0, 0, 0.06)'
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: quizResults.color || 'var(--primary-blue)' }}
                  >
                    <Compass className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Recommendations for you
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissRecommendations}
                  className="shrink-0 -mt-1 -mr-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                Based on your &ldquo;{quizResults.theme}&rdquo; results:
              </p>
              <div className="space-y-2">
                {quizResults.recommendations?.slice(0, 4).map((rec, idx) => (
                  <button
                    key={idx}
                    onClick={() => rec.path && navigate(rec.path)}
                    className="w-full p-3 rounded-lg border text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    style={{ borderColor: 'var(--border-color)' }}
                  >
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{rec.name}</span>
                    <ChevronRight className="w-4 h-4" style={{ color: quizResults.color }} />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Primary Action Accordions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {ACCORDION_SECTIONS.map((section) => {
            const IconComponent = section.icon;
            const isOpen = openAccordion === section.id;
            const isSecondaryOpen = openSecondary === section.id;
            const cardOverlayUrl = getCardOverlayUrl(section.id);
            const cardOverlayOpacity = getCardOverlayOpacity(section.id);
            const sectionCardSettings = hubCardsSettings?.[section.id];
            const hasCustomCardSettings = sectionCardSettings?.background_color || sectionCardSettings?.overlay_image_id;
            
            return (
              <Card 
                key={section.id}
                className="shadow-md hover:shadow-lg transition-shadow duration-200"
                style={getCustomCardStyle(section, isOpen)}
                data-custom-card={hasCustomCardSettings ? 'true' : undefined}
              >
                {/* Card Overlay Image - per card */}
                {cardOverlayUrl && (
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${cardOverlayUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: cardOverlayOpacity,
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                )}
                {/* Accordion Header */}
                <button
                  onClick={() => handleAccordionClick(section.id)}
                  className="w-full p-4 sm:p-5 flex items-center justify-between relative"
                  style={{ zIndex: 1 }}
                  data-testid={`accordion-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
                      style={{ background: section.color }}
                    >
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <span className="font-semibold text-lg sm:text-xl block" style={{ color: 'var(--text-primary)' }}>
                        {section.title}
                      </span>
                      <span className="text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
                        {section.subtitle}
                      </span>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    style={{ color: 'var(--text-secondary)' }}
                  />
                </button>

                {/* Accordion Content */}
                {isOpen && (
                  <CardContent className="pt-0 pb-4 px-4 sm:px-5 relative" style={{ zIndex: 1 }}>
                    {/* Primary Tools */}
                    <p className="text-sm uppercase tracking-wide mb-3 font-medium" style={{ color: isDarkMode ? '#a1a1aa' : '#52525b' }}>
                      Access essential support tools
                    </p>
                    <div className="space-y-2 mb-4">
                      {section.primaryTools.map((tool, idx) => {
                        const ToolIcon = tool.icon;
                        const isGuidedMode = tool.path === '/guided-mode';
                        // SINGLE SOURCE OF TRUTH for locked state
                        const isToolLocked = tool.isPremium && !is_premium;
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => handleToolClick(tool)}
                            className={`w-full p-3 sm:p-4 rounded-lg border text-left flex items-start gap-3 hover:shadow-md transition-all relative overflow-hidden ${isGuidedMode ? 'guided-mode-shimmer' : ''}`}
                            style={{ 
                              background: isDarkMode ? '#1a3330' : 'var(--bg-card)',
                              borderColor: isGuidedMode ? (isDarkMode ? 'rgba(20, 184, 166, 0.6)' : 'rgba(20, 184, 166, 0.5)') : section.color,
                              opacity: isToolLocked ? 0.7 : 1
                            }}
                            data-testid={`tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {/* Locked overlay for premium tools */}
                            <LockedOverlay isLocked={isToolLocked} size="small" />
                            
                            {/* Shimmer effect overlay for Guided Mode */}
                            {isGuidedMode && (
                              <div 
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                  background: `linear-gradient(
                                    110deg,
                                    transparent 20%,
                                    ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.4)'} 40%,
                                    ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.6)'} 50%,
                                    ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.4)'} 60%,
                                    transparent 80%
                                  )`,
                                  backgroundSize: '200% 100%',
                                  animation: 'shimmer 8s ease-in-out infinite',
                                  zIndex: 1
                                }}
                              />
                            )}
                            {ToolIcon && (
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: section.color + '20' }}
                              >
                                <ToolIcon className="w-4 h-4" style={{ color: section.color }} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="font-medium block" style={{ color: 'var(--text-primary)' }}>
                                {tool.name}
                                {tool.isPremium && !is_premium && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Premium</span>}
                              </span>
                              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {tool.description}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: section.color }} />
                          </button>
                        );
                      })}
                    </div>

                    {/* Secondary Tools (if any) */}
                    {section.secondaryTools.length > 0 && section.secondaryLabel && (
                      <div className="border-t pt-3" style={{ borderColor: 'var(--border-color)' }}>
                        <button
                          onClick={() => handleSecondaryClick(section.id)}
                          className="w-full flex items-center justify-between py-2"
                        >
                          <span className="text-base" style={{ color: 'var(--text-secondary)' }}>
                            {section.secondaryLabel}
                          </span>
                          <ChevronDown 
                            className={`w-4 h-4 transition-transform duration-200 ${isSecondaryOpen ? 'rotate-180' : ''}`}
                            style={{ color: 'var(--text-secondary)' }}
                          />
                        </button>
                        
                        {isSecondaryOpen && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                            {section.secondaryTools.map((tool, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (tool.scrollToSection) {
                                    navigate(`${tool.path}?section=${tool.scrollToSection}`);
                                  } else {
                                    navigate(tool.path);
                                  }
                                }}
                                className="p-3 rounded-lg border hover:shadow-sm transition-all flex items-center justify-center"
                                style={{ 
                                  background: isDarkMode ? '#1a3330' : 'var(--bg-card)',
                                  borderColor: 'var(--border-color)'
                                }}
                              >
                                <span className="font-medium text-sm text-center" style={{ color: 'var(--text-primary)' }}>
                                  {tool.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
          
          {/* Resources Accordion Card - rendered in same container for consistent loading */}
          <ResourcesAccordionCard 
            hubCardsSettings={hubCardsSettings}
            cardOverlayUrls={cardOverlayUrls}
            isDarkMode={isDarkMode}
            resourcesAccordionOpen={resourcesAccordionOpen}
            setResourcesAccordionOpen={setResourcesAccordionOpen}
            navigate={navigate}
          />
        </div>

        {/* Support Resources Modal */}
        <SupportResourcesModal 
          isOpen={showSupportModal} 
          onClose={() => setShowSupportModal(false)} 
        />
        
        {/* Guided Mode Info Modal (for guests) */}
        {showGuidedModeInfo && (
          <GuidedModeInfoModal
            onClose={() => setShowGuidedModeInfo(false)}
            isGuest={isGuest}
          />
        )}
        
        {/* Paywall Modal - shows when clicking a locked premium tool */}
        {showPaywall && (
          <PaywallModal 
            isOpen={showPaywall} 
            onClose={() => setShowPaywall(false)}
            customMessage="This tool requires premium access. Unlock all tools and features with a subscription."
          />
        )}
      </div>
    </div>
  );
}
